import type { RecordsGatewayPort } from "@/application/records/ports/RecordsGatewayPort";
import { RetryService } from "@/application/common/services/RetryService";
import { MetricsService } from "@/application/common/services/MetricsService";
import { ErrorHandlerService } from "@/application/common/services/ErrorHandlerService";
import {
  EventBus,
  RECORD_UNLINKED_EVENT,
  RECORD_UNLINK_FAILED_EVENT,
} from "@/application/common/events/EventBus";
import { assertNoDemoReportMutations } from "./demoReportMutationGuard";

export interface RemoveFromProjectInputDto {
  recordIds: string[];
  projectId?: string;
  userId?: string;
}

/**
 * Use case: removes records from their assigned project (sets project_id to NULL)
 * Clean Architecture - Application Layer с улучшениями
 */
export class RemoveFromProjectUseCase {
  constructor(
    private readonly gateway: RecordsGatewayPort,
    private readonly retryService: RetryService,
    private readonly metricsService: MetricsService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly eventBus: EventBus
  ) {}

  async execute(input: RemoveFromProjectInputDto): Promise<void> {
    const { recordIds, projectId, userId } = input;
    if (!recordIds.length) {
      return;
    }

    const startTime = Date.now();
    const context = {
      operation: "removeFromProject",
      userId,
      recordIds,
      projectId,
    };

    try {
      await this.retryService.executeWithRetry(async () => {
        const request = {
          change_names: [],
          for_archive: [],
          for_delete: [],
          delete_link: recordIds,
        };
        assertNoDemoReportMutations(request);
        await this.gateway.updateRecords(request);
      }, "removeFromProject");

      const duration = Date.now() - startTime;
      this.metricsService.recordMetric({
        operation: "removeFromProject",
        duration,
        success: true,
        metadata: { recordCount: recordIds.length, projectId },
      });

      // Публикуем событие успеха
      await this.eventBus.publish({
        type: RECORD_UNLINKED_EVENT,
        payload: { recordIds, projectId },
        timestamp: Date.now(),
        userId,
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorObj =
        error instanceof Error ? error : new Error(String(error));

      this.metricsService.recordMetric({
        operation: "removeFromProject",
        duration,
        success: false,
        error: errorObj.message,
        metadata: { recordCount: recordIds.length, projectId },
      });

      // Публикуем событие ошибки
      await this.eventBus.publish({
        type: RECORD_UNLINK_FAILED_EVENT,
        payload: { recordIds, projectId, error: errorObj.message },
        timestamp: Date.now(),
        userId,
      });

      const userMessage = this.errorHandler.handleError(errorObj, context);
      throw new Error(userMessage);
    }
  }
}
